import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/people/Pagination';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import {
  MeetingDrawer,
  type MeetingDrawerMode,
} from '../components/academics/MeetingDrawer';
import {
  type BatchRecord,
  type MeetingReport,
} from '../components/academics/types';
import type { HouseholdRecord } from '../components/people/types';
import { formatDate } from '../lib/format';

type FilterKind = 'all' | 'mine';

const PAGE_SIZE = 20;

const MANAGE_ROLES: ReadonlyArray<string> = [
  'super_admin',
  'academic_admin',
  'nalim',
  'teacher',
];

export const Route = createFileRoute('/_authed/academics/meetings')({
  component: MeetingsPage,
});

function MeetingsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage = role !== null && MANAGE_ROLES.includes(role);

  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<FilterKind>('all');
  const [drawer, setDrawer] = useState<MeetingDrawerMode | null>(null);

  const listQuery = useQuery({
    queryKey: ['meetings', 'list', page] as const,
    queryFn: ({ signal }) =>
      apiRequest<{
        items: MeetingReport[];
        page: number;
        pageSize: number;
        total: number;
      }>('/api/meetings', { signal, query: { page, pageSize: PAGE_SIZE } }),
    placeholderData: (prev) => prev,
  });

  const batchesQuery = useQuery({
    queryKey: ['batches', 'meetings-lookup'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: BatchRecord[] }>('/api/batches', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const householdsQuery = useQuery({
    queryKey: ['households', 'meetings-lookup'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: HouseholdRecord[]; total: number }>('/api/households', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const detailQuery = useQuery({
    queryKey: ['meetings', 'detail', drawer?.kind === 'edit' ? drawer.id : null] as const,
    enabled: drawer?.kind === 'edit',
    queryFn: ({ signal }) => {
      if (drawer?.kind !== 'edit') throw new Error('No meeting selected');
      return apiRequest<MeetingReport>(`/api/meetings/${drawer.id}`, { signal });
    },
  });

  const batchLookup = useMemo(() => {
    const map = new Map<string, BatchRecord>();
    for (const b of batchesQuery.data?.items ?? []) map.set(b.id, b);
    return map;
  }, [batchesQuery.data]);

  const householdLookup = useMemo(() => {
    const map = new Map<string, HouseholdRecord>();
    for (const h of householdsQuery.data?.items ?? []) map.set(h.id, h);
    return map;
  }, [householdsQuery.data]);

  // "Mine" is client-side because the API doesn't expose a createdBy filter.
  const filteredItems = useMemo(() => {
    const items = listQuery.data?.items ?? [];
    if (filter === 'all') return items;
    if (filter === 'mine' && currentUserId) {
      return items.filter((m) => m.createdBy === currentUserId);
    }
    return items;
  }, [listQuery.data, filter, currentUserId]);

  const columns = useMemo<ColumnDef<MeetingReport, unknown>[]>(() => {
    const base: ColumnDef<MeetingReport, unknown>[] = [
      {
        id: 'meetingAt',
        header: 'When',
        cell: (info) => (
          <span className="font-medium text-[color:var(--color-text)]">
            {formatDate(info.row.original.meetingAt, { withTime: true })}
          </span>
        ),
      },
      {
        id: 'context',
        header: 'Context',
        cell: (info) => {
          const m = info.row.original;
          if (m.batchId) {
            const b = batchLookup.get(m.batchId);
            return (
              <span className="text-[color:var(--color-text-muted)]">
                Batch · {b ? b.name : m.batchId.slice(0, 8)}
              </span>
            );
          }
          if (m.householdId) {
            const h = householdLookup.get(m.householdId);
            return (
              <span className="text-[color:var(--color-text-muted)]">
                Family · {h ? h.primaryContactName : m.householdId.slice(0, 8)}
              </span>
            );
          }
          return <span className="text-[color:var(--color-text-muted)]">—</span>;
        },
      },
      {
        id: 'summary',
        header: 'Summary',
        cell: (info) => {
          const text = info.row.original.summary;
          const trimmed = text.length > 90 ? `${text.slice(0, 87)}…` : text;
          return (
            <span className="text-[color:var(--color-text)]">{trimmed}</span>
          );
        },
      },
      {
        id: 'attendees',
        header: 'Attendees',
        cell: (info) => (
          <span className="tabular-nums text-[color:var(--color-text-muted)]">
            {info.row.original.attendees.length}
          </span>
        ),
      },
    ];

    if (canManage) {
      base.push({
        id: 'actions',
        header: '',
        cell: (info) => (
          <div className="flex justify-end">
            <EditIconButton
              label={`Edit meeting ${formatDate(info.row.original.meetingAt, { withTime: true })}`}
              onClick={() => setDrawer({ kind: 'edit', id: info.row.original.id })}
            />
          </div>
        ),
      });
    }

    return base;
  }, [batchLookup, householdLookup, canManage]);

  return (
    <ListOrOutlet basePath="/academics/meetings">
    <ListShell
      title="Meetings"
      description="Parent calls, batch reviews, and home-visit reports."
      primaryAction={
        <Button
          onClick={() => setDrawer({ kind: 'create' })}
          icon={<Plus size={16} aria-hidden="true" />}
        >
          Log meeting
        </Button>
      }
      filters={<MeetingFilterChips value={filter} onChange={setFilter} />}
    >
      {listQuery.isError ? (
        <ErrorState
          title="Could not load meetings"
          description={
            listQuery.error instanceof ApiError ? listQuery.error.message : undefined
          }
          onRetry={() => listQuery.refetch()}
        />
      ) : (
        <>
          <DataTable<MeetingReport>
            data={filteredItems}
            columns={columns}
            isLoading={listQuery.isLoading && !listQuery.data}
            getRowId={(row) => row.id}
            onRowClick={(row) =>
              void navigate({
                to: '/academics/meetings/$meetingId',
                params: { meetingId: row.id },
              })
            }
            emptyState={
              <EmptyState
                title="No meetings logged yet"
                description="Capture parent calls and batch reviews so the team has shared context."
                action={
                  <Button
                    onClick={() => setDrawer({ kind: 'create' })}
                    icon={<Plus size={16} aria-hidden="true" />}
                  >
                    Log meeting
                  </Button>
                }
              />
            }
          />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={listQuery.data?.total ?? 0}
            onPageChange={setPage}
          />
        </>
      )}

      <MeetingDrawer
        mode={drawer}
        onClose={() => setDrawer(null)}
        initial={detailQuery.data}
        isLoadingInitial={drawer?.kind === 'edit' && detailQuery.isLoading}
        loadError={detailQuery.error instanceof Error ? detailQuery.error : null}
        batches={batchesQuery.data?.items ?? []}
        households={householdsQuery.data?.items ?? []}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['meetings'] });
          toast('Meeting saved.', { variant: 'success' });
          setDrawer(null);
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}

function MeetingFilterChips({
  value,
  onChange,
}: {
  value: FilterKind;
  onChange: (next: FilterKind) => void;
}) {
  const options: ReadonlyArray<{ value: FilterKind; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'mine', label: 'Mine' },
  ];
  return (
    <div role="tablist" aria-label="Filter meetings" className="flex flex-wrap items-center gap-2">
      {options.map((opt) => {
        const isActive = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(opt.value)}
            className={
              isActive
                ? 'inline-flex items-center rounded-full border border-gold-500 bg-gold-50 px-3 py-1 text-xs font-medium text-gold-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500'
                : 'inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-1 text-xs font-medium text-[color:var(--color-text-muted)] hover:border-gold-500 hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500'
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
